import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import ProspectEmailFormContent from '~/src/common/components/ProspectEmailForm/ProspectEmailFormContent';
import { useApiEcomClientError } from '~/src/common/services/error-handler/error-handler';
import I18n from '~/src/common/services/I18n/I18n';
import Tracker from '~/src/common/services/Tracker';
import { DeliveryAddress } from '~/src/common/typings/cart';
import { useAddProspectAddress } from '~/src/queries/api-ecom/generated/api-ecom';
import { ApiEcomError } from '~/src/queries/services/client';
import { User } from '~/src/queries/types/user';
import { QUERY_KEYS } from '~/src/screens/App/queries/queries';
import { Address } from '~/src/typings/addresses/types';

import {
  Container,
  CollectShopsButton,
  Separator,
  ContainerRow,
  ContainerButtonRow,
} from './layout';
import { ContactEmailFormData } from './types';

interface Props {
  address: Address | DeliveryAddress | undefined;
  handleClickCollectShopsButton?: () => void;
  className?: string;
}

const ProspectEmailForm = ({ address, handleClickCollectShopsButton, className }: Props) => {
  const [isEmailSent, setIsEmailSent] = useState(false);
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User>(QUERY_KEYS.user);
  const { register, handleSubmit, formState } = useForm<ContactEmailFormData>({
    mode: 'all',
    defaultValues: { email: user?.email },
  });
  const handleError = useApiEcomClientError();
  const { mutate: addProspectAddress, isLoading: isLoadingWaitingList } = useAddProspectAddress({
    mutation: {
      onSuccess: () => setIsEmailSent(true),
      onError: error => {
        handleError(error as ApiEcomError);
      },
    },
  });

  useEffect(() => {
    setIsEmailSent(false);
  }, [address]);

  const emailError = formState.errors?.email?.message;

  const handleEmailSubmit = handleSubmit((values: ContactEmailFormData) => {
    const { email } = values;
    Tracker.sendEvent('click cta send my e-mail');
    addProspectAddress({
      data: {
        email,
        address: address?.formattedAddress ?? '',
        postalCode: address?.addressComponents.postalCode ?? '',
      },
    });
  });

  return (
    <Container className={className}>
      <ContainerRow>
        {I18n.t('prospect-email-form.not-delivered-waiting-list1')}
        <span>{I18n.t('prospect-email-form.not-delivered-waiting-list2')}</span>
      </ContainerRow>
      <Separator />
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises  -- auto-ignored when updating eslint
        onSubmit={handleEmailSubmit}>
        <ContainerRow>
          <ProspectEmailFormContent
            emailError={emailError}
            isLoadingWaitingList={isLoadingWaitingList}
            register={register}
            isEmailSent={isEmailSent}
          />
        </ContainerRow>
      </form>
      {handleClickCollectShopsButton ? (
        <>
          <Separator />
          <ContainerButtonRow>
            <CollectShopsButton
              variant="transparent"
              rightIcon="marker-normal"
              iconSize={23}
              onClick={handleClickCollectShopsButton}>
              {I18n.t('prospect-email-form.button-label')}
            </CollectShopsButton>
          </ContainerButtonRow>
        </>
      ) : null}
    </Container>
  );
};
export default ProspectEmailForm;
